import { axiosConfig } from "@/services/axiosConfig";

interface SendOTPResponse {
  message: string;
  code_reference: string;
}

interface VerifyOTPResponse {
  refresh: string;
  access: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    phone_code: string;
    user_profile_completed: boolean;
    tenant_username: string;
    full_name: string;
    username: string;
  };
}

class OTPService {
  /**
   * Send OTP for login (existing user).
   * POST /api/auth/login/ with { phone_number, phone_code }
   */
  sendOTPLogin = async (
    phoneNumber: string,
    phoneCode: string
  ): Promise<SendOTPResponse> => {
    if (!phoneNumber) {
      throw new Error("Phone number is required");
    }
    if (!phoneCode) {
      throw new Error("Phone code is required");
    }
    const requestPayload = { phone_number: phoneNumber, phone_code: phoneCode };
    const response = await axiosConfig.post<SendOTPResponse>(
      "/api/auth/login/",
      requestPayload
    );
    return response.data;
  };

  /**
   * Verify OTP and complete login.
   * POST /api/auth/login/ with { otp, code_reference }
   */
  verifyOTPLogin = async (
    otp: string,
    codeReference: string
  ): Promise<VerifyOTPResponse> => {
    if (!otp) {
      throw new Error("OTP is required");
    }
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    try {
      const requestPayload = {
        otp: otp.trim(),
        code_reference: codeReference.trim(),
      };
      const response = await axiosConfig.post<VerifyOTPResponse>(
        "/api/auth/login/",
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      const data = error?.response?.data;
      const hasOtpError =
        data &&
        typeof data === "object" &&
        (Object.prototype.hasOwnProperty.call(data, "otp") ||
          Object.prototype.hasOwnProperty.call(data, "OTP"));
      if (hasOtpError) {
        throw new Error("Invalid OTP");
      }
      const msg =
        data?.message ??
        data?.detail ??
        data?.error ??
        error?.message ??
        "Failed to verify OTP.";
      throw new Error(typeof msg === "string" ? msg : "Failed to verify OTP.");
    }
  };

  /**
   * Send OTP for signup (new user)
   * POST /api/auth/ with { phone_number: "...", phone_code: "..." }
   * @param phoneNumber - 10 digit phone number
   * @param phoneCode - Phone code UUID (required by API, e.g. from Postman)
   * @returns Response with message and code_reference
   */
  sendOTP = async (
    phoneNumber: string,
    phoneCode: string
  ): Promise<SendOTPResponse> => {
    if (!phoneNumber) {
      throw new Error("Phone number is required");
    }
    if (!phoneCode) {
      throw new Error("Phone code is required");
    }

    try {
      const requestPayload: any = {
        phone_number: phoneNumber,
        phone_code: phoneCode,
      };

      console.log("📤 Sending OTP request:", requestPayload);

      const response = await axiosConfig.post<SendOTPResponse>(
        "/api/auth/",
        requestPayload
      );

      console.log("✅ OTP Response:", response.data);
      return response.data;
    } catch (error: any) {
      // Log error for debugging - check both error object and response
      console.error("❌ OTP Send Error:", error);
      console.error("📋 Error Response:", error?.response?.data);
      console.error("📊 Error Status:", error?.response?.status);
      console.error("🔍 Full Error Object:", error);

      if (error?.response?.data) {
        const errorData = error.response.data;
        console.error(
          "🔍 Full Error Response Data:",
          JSON.stringify(errorData, null, 2)
        );
        console.error("🔍 Error Data Keys:", Object.keys(errorData));
        console.error("🔍 Error Data Type:", typeof errorData);
        console.error("🔍 Error Data Entire Object:", errorData);

        // Log every single property to see what's there
        if (typeof errorData === "object" && errorData !== null) {
          console.error("🔍 All Error Properties:");
          for (const key in errorData) {
            console.error(
              `  - ${key}:`,
              errorData[key],
              `(type: ${typeof errorData[key]})`
            );
          }
        }

        // Check for field-specific errors in various formats
        // Format 1: {email: ["This field is required."], mobile_number: ["Invalid format"]}
        // Format 2: {error: {email: "This field is required."}}
        // Format 3: {non_field_errors: ["Some error"]}

        const fieldErrors: string[] = [];

        // Check top-level fields (excluding standard error keys)
        Object.entries(errorData).forEach(([key, value]) => {
          if (!["error", "message", "code", "detail"].includes(key)) {
            if (Array.isArray(value)) {
              fieldErrors.push(`${key}: ${value.join(", ")}`);
            } else if (typeof value === "string") {
              fieldErrors.push(`${key}: ${value}`);
            } else if (typeof value === "object" && value !== null) {
              // Nested object
              Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                if (Array.isArray(nestedValue)) {
                  fieldErrors.push(
                    `${key}.${nestedKey}: ${nestedValue.join(", ")}`
                  );
                } else {
                  fieldErrors.push(`${key}.${nestedKey}: ${nestedValue}`);
                }
              });
            }
          }
        });

        // Check nested error object
        if (errorData.error && typeof errorData.error === "object") {
          Object.entries(errorData.error).forEach(([key, value]) => {
            if (!["code", "message"].includes(key)) {
              if (Array.isArray(value)) {
                fieldErrors.push(`${key}: ${value.join(", ")}`);
              } else {
                fieldErrors.push(`${key}: ${value}`);
              }
            }
          });
        }

        if (fieldErrors.length > 0) {
          console.error("⚠️ Field-specific errors found:", fieldErrors);
          throw new Error(`Validation Error - ${fieldErrors.join("; ")}`);
        }

        // Check for non_field_errors
        if (
          errorData.non_field_errors &&
          Array.isArray(errorData.non_field_errors)
        ) {
          console.error("⚠️ Non-field errors:", errorData.non_field_errors);
          throw new Error(`Error: ${errorData.non_field_errors.join("; ")}`);
        }
      }

      // Also check the Network tab - the error might be in response.data
      if (error?.response?.status === 400) {
        console.error(
          "⚠️ 400 Bad Request - Please check Network tab in DevTools (F12) → Network → Click on the failed request → Response tab"
        );
      }

      // Extract error message from various possible formats
      let errorMessage = "Failed to send OTP. Please try again.";

      if (error?.response?.data) {
        const data = error.response.data;
        // Try multiple possible error formats
        const extractedMessage =
          data.message ??
          data.error?.message ??
          (typeof data.error === "string" ? data.error : null) ??
          data.detail ??
          data.msg ??
          (Array.isArray(data.non_field_errors)
            ? data.non_field_errors[0]
            : null) ??
          (typeof data === "string" ? data : null);

        if (extractedMessage) {
          errorMessage = extractedMessage;
        } else {
          // If we can't extract a message, show the whole error object
          errorMessage = JSON.stringify(data, null, 2);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // If the error is about invalid or expired OTP, normalize it
      if (
        typeof errorMessage === "string" &&
        (errorMessage.toLowerCase().includes("invalid") ||
          errorMessage.toLowerCase().includes("expired")) &&
        errorMessage.toLowerCase().includes("otp")
      ) {
        errorMessage = "invalid otp";
      }

      // Always throw to ensure the error is caught by the UI
      const finalError = new Error(errorMessage);
      (finalError as any).response = error?.response; // Preserve response for debugging
      throw finalError;
    }
  };

  /**
   * Verify OTP and login/register
   * POST /api/auth/ with { otp: "...", code_reference: "..." }
   * @param otp - 6 digit OTP
   * @param codeReference - Code reference from send OTP response (required)
   * @returns Response with access token, refresh token, and user data
   */
  verifyOTP = async (
    otp: string,
    codeReference: string
  ): Promise<VerifyOTPResponse> => {
    if (!otp) {
      throw new Error("OTP is required");
    }
    if (!codeReference) {
      throw new Error("Code reference is required");
    }

    try {
      // Build request payload for verification - only otp and code_reference needed
      const requestPayload: any = {
        otp: otp.trim(), // Make sure OTP is trimmed
        code_reference: codeReference.trim(), // Make sure code_reference is trimmed
      };

      console.log("📤 Verifying OTP request:", requestPayload);
      console.log("📋 OTP value:", `"${otp}"`); // Log with quotes to see any hidden characters
      console.log("📋 Code reference value:", `"${codeReference}"`);

      // Use axiosConfig - interceptor now excludes login endpoint from Authorization header
      const response = await axiosConfig.post<VerifyOTPResponse>(
        "/api/auth/",
        requestPayload
      );

      console.log("✅ Verify OTP Response:", response?.data);
      return response?.data;
    } catch (error: any) {
      // Log error for debugging - check both error object and response
      console.error("OTP Verify Error:", error);
      console.error("Error Response:", error?.response?.data);
      console.error(
        "Full Error:",
        JSON.stringify(error?.response?.data, null, 2)
      );

      // Extract error message from various possible formats
      let errorMessage = "Failed to verify OTP. Please try again.";

      if (error?.response?.data) {
        const data = error.response.data;
        // Try multiple possible error formats
        errorMessage =
          data.message ??
          data.error?.message ??
          data.error ??
          data.detail ??
          data.msg ??
          (Array.isArray(data.non_field_errors)
            ? data.non_field_errors[0]
            : null) ??
          // Check for field errors (DRF format)
          (data.otp
            ? Array.isArray(data.otp)
              ? data.otp[0]
              : data.otp
            : null) ??
          (typeof data === "string" ? data : null) ??
          errorMessage;

        // If the error is about invalid or expired OTP, normalize it as requested
        if (
          typeof errorMessage === "string" &&
          (errorMessage.toLowerCase().includes("invalid") ||
            errorMessage.toLowerCase().includes("expired")) &&
          errorMessage.toLowerCase().includes("otp")
        ) {
          errorMessage = "invalid otp";
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Always throw to ensure the error is caught by the UI
      const finalError = new Error(errorMessage);
      (finalError as any).response = error?.response; // Preserve response for debugging
      throw finalError;
    }
  };
}

const instance = new OTPService();
export default instance;
